import React, { useEffect, useState } from "react";
import { Container, Row, Col, Accordion, Alert } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFolder } from "@fortawesome/free-solid-svg-icons";
import CustomButton from "../../common-components/button/Button.jsx";
import { setCurrentStep, setSelectedStructure } from "../../state/reducers/architectureConversion";
import axios from "../../utils/axios-interceptor.js";

function ArchFolderStructure({ sourceFiles, gitRepoUrl }) {
  const [folderStructure, setFolderStructure] = useState([]);
  const [selectedStructure, setLocalSelectedStructure] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  const processGitHubFiles = (files) => {
    console.log("Processing GitHub files:", files);
    const root = [];
    
    if (!Array.isArray(files)) {
      console.error("Invalid files data received:", files);
      throw new Error("Invalid files data structure received from server");
    }

    // Build path for each file/folder
    const processItem = (item, parentPath = "") => {
      if (!item || typeof item !== "object") {
        console.warn("Skipping invalid item:", item);
        return null;
      }

      const fullPath = parentPath ? `${parentPath}/${item.name}` : item.name;

      if (item.type === "folder" && Array.isArray(item.children)) {
        const processedChildren = item.children
          .map(child => processItem(child, fullPath))
          .filter(Boolean);

        return {
          name: item.name,
          type: "folder",
          path: fullPath,
          id: `folder-${fullPath}`,
          children: processedChildren
        };
      } else if (item.type === "file") {
        return {
          name: item.name,
          type: "file",
          path: fullPath,
          id: `file-${fullPath}`
        };
      }

      return null;
    };

    // Process all root level items
    files.forEach(item => {
      const processedItem = processItem(item);
      if (processedItem) {
        root.push(processedItem);
      }
    });

    // Sort the structure
    const sortStructure = (items) => {
      items.sort((a, b) => {
        // Folders come before files
        if (a.type === "folder" && b.type === "file") return -1;
        if (a.type === "file" && b.type === "folder") return 1;
        // Alphabetical sort within the same type
        return a.name.localeCompare(b.name);
      });

      // Recursively sort children
      items.forEach(item => {
        if (item.type === "folder" && Array.isArray(item.children)) {
          sortStructure(item.children);
        }
      });

      return items;
    };

    console.log("Final processed structure:", sortStructure(root));
    return sortStructure(root);
  };

  const processLocalFiles = (files) => {
    const root = [];
    files.forEach(file => {
      const parts = file.webkitRelativePath.split("/");
      let currentLevel = root;
      let currentPath = "";

      parts.forEach((part, index) => {
        currentPath += (index > 0 ? "/" : "") + part;
        const existingItem = currentLevel.find(item => item.name === part);

        if (index === parts.length - 1) {
          if (!existingItem) {
            currentLevel.push({
              name: part,
              type: "file",
              path: currentPath,
              id: `file-${currentPath}`
            });
          }
        } else {
          if (existingItem) {
            currentLevel = existingItem.children;
          } else {
            const newFolder = {
              name: part,
              type: "folder",
              children: [],
              path: currentPath,
              id: `folder-${currentPath}`
            };
            currentLevel.push(newFolder);
            currentLevel = newFolder.children;
          }
        }
      });
    });
    return root;
  };

  const handleCheckboxChange = (item, isChecked) => {
    const updateSelection = (currentItem, isChecked) => {
      if (currentItem.type === "file") {
        setSelectedItems(prev =>
          isChecked
            ? [...prev, currentItem]
            : prev.filter(i => i.path !== currentItem.path)
        );
      } else if (currentItem.type === "folder") {
        if (currentItem.children) {
          currentItem.children.forEach(child => updateSelection(child, isChecked));
        }
      }
    };

    updateSelection(item, isChecked);
  };

  const handleNext = () => {
    dispatch(setSelectedStructure(selectedStructure));
    dispatch(setCurrentStep(3));
  };

  const handleBack = () => {
    dispatch(setCurrentStep(1));
  };

  const handleAddAll = () => {
    setLocalSelectedStructure(folderStructure);
  };

  const handleAddSelected = () => {
    const buildSelectedStructure = (items) => {
      const structure = [];
      items.forEach(item => {
        const parts = item.path.split("/");
        let currentLevel = structure;
        parts.forEach((part, index) => {
          let existingItem = currentLevel.find(i => i.name === part);
          if (!existingItem) {
            existingItem = {
              name: part,
              type: index === parts.length - 1 ? item.type : "folder",
              path: parts.slice(0, index + 1).join("/"),
              id: `${index === parts.length - 1 ? item.type : "folder"}-${parts.slice(0, index + 1).join("/")}`,
              children: []
            };
            currentLevel.push(existingItem);
          }
          if (index < parts.length - 1) {
            currentLevel = existingItem.children;
          }
        });
      });
      return structure;
    };

    setLocalSelectedStructure(buildSelectedStructure(selectedItems));
    setSelectedItems([]);
  };

  const handleRemoveAll = () => {
    setLocalSelectedStructure([]);
    setSelectedItems([]);
  };

  const handleRemoveSelected = () => {
    const removeItems = (structure, itemsToRemove) => {
      return structure.filter(item => {
        if (itemsToRemove.some(i => i.path === item.path)) {
          return false;
        }
        if (item.type === "folder" && item.children) {
          item.children = removeItems(item.children, itemsToRemove);
          return item.children.length > 0;
        }
        return true;
      });
    };

    const newSelectedStructure = removeItems(selectedStructure, selectedItems);
    setLocalSelectedStructure(newSelectedStructure);
    setSelectedItems([]);
  };

  useEffect(() => {
    const fetchGitHubStructure = async (url) => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await axios.get(`/github-structure?url=${encodeURIComponent(url)}`);
        console.log("GitHub API Raw Response:", response);
        
        if (!response.data) {
          throw new Error("No data received from server");
        }

        if (!Array.isArray(response.data)) {
          throw new Error("Invalid data format received from server");
        }

        if (response.data.length === 0) {
          throw new Error("Repository appears to be empty");
        }

        console.log("GitHub API Data:", response.data);
        const processedStructure = processGitHubFiles(response.data);
        
        if (processedStructure.length === 0) {
          throw new Error("No valid files or folders found in the repository");
        }
        
        console.log("Setting folder structure:", processedStructure);
        setFolderStructure(processedStructure);
      } catch (error) {
        console.error("Error fetching GitHub structure:", error);
        setError(`Failed to fetch GitHub structure: ${error.message}`);
        setFolderStructure([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (gitRepoUrl) {
      console.log("Fetching GitHub structure for URL:", gitRepoUrl);
      fetchGitHubStructure(gitRepoUrl);
    } else if (sourceFiles.length > 0) {
      console.log("Processing local files:", sourceFiles);
      const processedStructure = processLocalFiles(sourceFiles);
      console.log("Setting folder structure from local files:", processedStructure);
      setFolderStructure(processedStructure);
    }
  }, [sourceFiles, gitRepoUrl]);

  const renderStructure = (structure, isSelected = false) => {
    console.log("Rendering structure:", structure, "isSelected:", isSelected);
    if (!Array.isArray(structure) || structure.length === 0) {
      console.log("Empty or invalid structure received in renderStructure");
      return null;
    }

    return structure.map((item) => {
      console.log("Rendering item:", item);
      return (
        <div key={item.id}>
          {item.type === "folder" ? (
            <Accordion defaultActiveKey={item.path}>
              <Accordion.Item eventKey={item.path}>
                <Accordion.Header>
                  <div className="folder">
                    <input 
                      type="checkbox" 
                      checked={isSelected 
                        ? selectedItems.some(i => i.path.startsWith(item.path))
                        : item.children.every(child => 
                          selectedItems.some(i => i.path.startsWith(child.path))
                        )
                      }
                      onChange={(e) => {
                        e.stopPropagation();
                        handleCheckboxChange(item, e.target.checked);
                      }}
                    />
                    <FontAwesomeIcon icon={faFolder} className="mr-2" />
                    {item.name}
                  </div>
                </Accordion.Header>
                <Accordion.Body className="select-item-list">
                  {item.children && item.children.length > 0 && 
                    renderStructure(item.children, isSelected)}
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          ) : (
            <div className="folder-items">
              <span className="check">
                <input 
                  type="checkbox" 
                  checked={selectedItems.some(i => i.path === item.path)}
                  onChange={(e) => handleCheckboxChange(item, e.target.checked)}
                />
              </span>
              {item.name}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div className="page-wrapper database-conversion-page px-3">
      <Container>
        <Row>
          <Col>
            <h2 className="page-title">
              Choose the folder/files you want to export
            </h2>
            {error && (
              <Alert variant="danger">
                {error}
              </Alert>
            )}
            <div className="arch-conversation-wrapper">
              <div className="arch-structure-wrapper">
                <div className="upload-structure-wrapper">
                  <h3 className="wrapper-title">Original Structure</h3>
                  {isLoading ? (
                    <p>Loading folder structure...</p>
                  ) : folderStructure.length > 0 ? (
                    <div className="folder-str">
                      {renderStructure(folderStructure)}
                      <CustomButton className="add-all-btn mt-4 me-3" label="Add All" onClick={handleAddAll} />
                      <CustomButton className="add-selected-btn mt-4 me-3" label="Add Selected" onClick={handleAddSelected} />
                    </div>
                  ) : (
                    <p>No folder structure available. {gitRepoUrl ? "Please check the GitHub repository URL and try again." : "Please upload files to see the structure."}</p>
                  )}
                </div>
                <div className="export-structure-wrapper">
                  <h3 className="wrapper-title">Selected Items</h3>
                  {selectedStructure.length > 0 ? (
                    <div className="folder-str">{renderStructure(selectedStructure, true)}</div>
                  ) : (
                    <p>No items selected.</p>
                  )}
                  <CustomButton className="add-all-btn mt-4 me-3" label="Remove All" onClick={handleRemoveAll} />
                  <CustomButton className="add-selected-btn mt-4 me-3" label="Remove Selected" onClick={handleRemoveSelected} />
                </div>
              </div>
            </div>
          </Col>
        </Row>
        <Row className="mt-4">
          <Col className="text-end">
            <CustomButton className="me-3" onClick={handleBack} label="Back" />
            <CustomButton onClick={handleNext} label="Next" disabled={isLoading || error} />
          </Col>
        </Row>
      </Container>
    </div>
  );  
}

export default ArchFolderStructure;